import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  TextField,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useAuth } from "../../context/AuthContext";
import useFetchObjects from "../../api/useFetchObjects";
import { Download } from "@mui/icons-material";
import { exportComprehensiveSaleReportToPDF, exportCustomerReportToPDF } from "../../utils/exportToPDF";
import { exportComprehensiveSaleReportToExcel } from "../../utils/exportSalesReport";

const ALL_SHOPS_OPTION = { id: "", name: "All Shops" };

const getRecordShopId = (record) =>
  record?.shop_details?.id ?? record?.shop ?? "";

const getRecordShopName = (record) =>
  record?.shop_details?.name || "Unknown Shop";

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const ALL_CUSTOMERS_OPTION = { id: "", name: "All Customers" };

const ComprehensiveSaleReport = () => {
  const auth = useAuth();
  const token = auth?.user?.token;

  const [tabValue, setTabValue] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedShop, setSelectedShop] = useState("");
  const [shopSearchTerm, setShopSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("simple");

  const dateParams = useMemo(() => {
    const params = ["page_size=all"];
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    return params;
  }, [startDate, endDate]);

  const queryString = useMemo(() => {
    const params = [...dateParams];
    if (selectedShop) params.push(`shop=${selectedShop}`);
    if (selectedCustomer) params.push(`customer=${selectedCustomer}`);
    return `?${params.join("&")}`;
  }, [dateParams, selectedShop, selectedCustomer]);

  const shopFinanceQueryString = useMemo(() => {
    const params = [...dateParams];
    if (selectedShop) params.push(`shop=${selectedShop}`);
    return `?${params.join("&")}`;
  }, [dateParams, selectedShop]);

  const stockQueryString = useMemo(() => {
    const params = ["page_size=all"];
    if (selectedShop) params.push(`shop=${selectedShop}`);
    return `?${params.join("&")}`;
  }, [selectedShop]);

  const { data: salesData, isLoading: salesLoading } = useFetchObjects(
    ["sales", queryString],
    `shop/sales/${queryString}`,
    token
  );
  const { data: paymentsData, isLoading: paymentsLoading } = useFetchObjects(
    ["sale-payments", queryString],
    `shop/sale_payments/${queryString}`,
    token
  );
  const { data: returnsData, isLoading: returnsLoading } = useFetchObjects(
    ["sale-returns", queryString],
    `shop/sale-returns/${queryString}`,
    token
  );
  const { data: balancePaymentsData, isLoading: balancePaymentsLoading } =
    useFetchObjects(
      ["customer-balance-payments", queryString],
      `shop/customer-balance-payments/${queryString}`,
      token
    );
  const { data: customersData } = useFetchObjects(
    ["customers", selectedShop],
    `shop/customers?page_size=all${selectedShop ? `&shop=${selectedShop}` : ""}`,
    token
  );
  const { data: shopsData, isLoading: shopsLoading } = useFetchObjects(
    ["shops", "sale-report-all-filter", shopSearchTerm],
    `shop/shop/?search=${encodeURIComponent(shopSearchTerm.trim())}&page_size=20`,
    token
  );
  const { data: purchasesData, isLoading: purchasesLoading } = useFetchObjects(
    ["purchases", "sale-report-benefit", shopFinanceQueryString],
    `shop/purchases/${shopFinanceQueryString}`,
    token
  );
  const { data: stocksData, isLoading: stocksLoading } = useFetchObjects(
    ["stocks", "sale-report-benefit", stockQueryString],
    `shop/stocks/${stockQueryString}`,
    token
  );

  // Fetch customer report data
  const { data: customerReportData } = useFetchObjects(
    ["customer-report", queryString],
    `shop/customer-report/${queryString}`,
    token
  );

  const sales = useMemo(
    () => salesData?.results || salesData || [],
    [salesData]
  );
  const payments = useMemo(
    () => paymentsData?.results || paymentsData || [],
    [paymentsData]
  );
  const returns = useMemo(
    () => returnsData?.results || returnsData || [],
    [returnsData]
  );
  const balancePayments = useMemo(
    () => balancePaymentsData?.results || balancePaymentsData || [],
    [balancePaymentsData]
  );
  const customers = useMemo(
    () => customersData?.results || customersData || [],
    [customersData]
  );
  const purchases = useMemo(
    () => purchasesData?.results || purchasesData || [],
    [purchasesData]
  );
  const stocks = useMemo(
    () => stocksData?.results || stocksData || [],
    [stocksData]
  );
  const baseShopOptions = useMemo(
    () => (Array.isArray(shopsData) ? shopsData : shopsData?.results || []),
    [shopsData]
  );
  const customerOptions = useMemo(
    () => [ALL_CUSTOMERS_OPTION, ...customers],
    [customers]
  );
  const shopOptions = useMemo(
    () => [ALL_SHOPS_OPTION, ...baseShopOptions],
    [baseShopOptions]
  );
  const selectedShopOption = useMemo(() => {
    return (
      shopOptions.find(
        (shop) => String(shop.id) === String(selectedShop)
      ) || ALL_SHOPS_OPTION
    );
  }, [shopOptions, selectedShop]);

  const selectedCustomerObj = useMemo(() => {
    if (!selectedCustomer) return null;
    return (
      customers.find((c) => String(c.id) === String(selectedCustomer)) || null
    );
  }, [customers, selectedCustomer]);
  const selectedCustomerOption = useMemo(() => {
    return (
      customerOptions.find(
        (customer) => String(customer.id) === String(selectedCustomer)
      ) || ALL_CUSTOMERS_OPTION
    );
  }, [customerOptions, selectedCustomer]);

  const eligibleBalancePayments = useMemo(() => {
    if (selectedCustomer) {
      return balancePayments.filter(
        (bp) =>
          String(bp.customer_details?.id) === String(selectedCustomer)
      );
    }
    if (selectedShop) {
      const customerIds = new Set((customers || []).map((c) => c.id));
      return balancePayments.filter((bp) =>
        customerIds.has(bp.customer_details?.id)
      );
    }
    return balancePayments;
  }, [balancePayments, selectedCustomer, selectedShop, customers]);

  const isLoading =
    salesLoading ||
    paymentsLoading ||
    returnsLoading ||
    balancePaymentsLoading ||
    purchasesLoading ||
    stocksLoading;

  const parseAmount = useCallback((value) => {
    if (!value && value !== 0) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "object" && value !== null) {
      const currencies = Object.keys(value);
      if (currencies.length > 0) {
        return parseFloat(value[currencies[0]]) || 0;
      }
    }
    const numStr = String(value).replace(/[^0-9.-]/g, "");
    return parseFloat(numStr) || 0;
  }, []);
  const getSaleSubtotal = useCallback(
    (sale) => parseAmount(sale?.total_amount ?? 0),
    [parseAmount]
  );
  const getSaleDiscount = useCallback(
    (sale) => parseAmount(sale?.discount ?? 0),
    [parseAmount]
  );
  // Sale total after discount (use effective_total_amount; fallback to total_amount)
  const getSaleTotal = useCallback(
    (sale) =>
      parseAmount(sale?.effective_total_amount ?? sale?.total_amount ?? 0),
    [parseAmount]
  );
  // `received_amount` is the amount paid on the sale itself; `total_paid`
  // contains later payment records. Both are needed for an accurate balance.
  const getSalePaid = useCallback(
    (sale) => {
      const paymentTotal = parseAmount(sale?.total_paid ?? 0);
      return paymentTotal + parseAmount(sale?.received_amount ?? 0);
    },
    [parseAmount]
  );

  const getPurchaseTotal = useCallback(
    (purchase) => parseAmount(purchase?.total_price ?? 0),
    [parseAmount]
  );
  const getStockValue = useCallback(
    (stock) => parseAmount(stock.buy_price) * Number(stock.quantity || 0),
    [parseAmount]
  );
  const formatProfit = useCallback(
    (value) => `${value < 0 ? "-" : ""}${Math.abs(value).toFixed(2)}`,
    []
  );

  // Filter sales by selected customer only
  const filteredSales = useMemo(() => {
    if (!selectedCustomer) return sales;
    return sales.filter(
      (sale) =>
        String(sale.customer_details?.id) === String(selectedCustomer)
    );
  }, [sales, selectedCustomer]);

  const filteredSaleIds = useMemo(
    () => new Set(filteredSales.map((s) => String(s.id))),
    [filteredSales]
  );

  // When a customer is selected, only show payments for that customer's sales
  const filteredPayments = useMemo(() => {
    if (!selectedCustomer && !selectedShop) return payments;
    return payments.filter((p) => filteredSaleIds.has(String(p.sale)));
  }, [payments, selectedCustomer, selectedShop, filteredSaleIds]);

  // When a customer or shop is selected, only show returns for those sales
  const filteredReturns = useMemo(() => {
    if (!selectedCustomer && !selectedShop) return returns;
    return returns.filter((r) => filteredSaleIds.has(String(r.sale)));
  }, [returns, selectedCustomer, selectedShop, filteredSaleIds]);

  // Older sales can contain the first receipt in `received_amount`, while
  // some newer versions also created a general balance-payment row for that
  // same receipt. Match those duplicate rows so a receipt is counted once.
  const matchedInitialReceiptPaymentIds = useMemo(() => {
    const matched = new Set();
    filteredSales.forEach((sale) => {
      const receipt = parseAmount(sale.received_amount);
      if (!receipt || !sale.customer_details?.id) return;
      const payment = eligibleBalancePayments.find((candidate) =>
        !matched.has(candidate.id) &&
        String(candidate.customer_details?.id) === String(sale.customer_details.id) &&
        parseAmount(candidate.amount) === receipt &&
        String(candidate.payment_date || "").slice(0, 10) === String(sale.sale_date || "")
      );
      if (payment) matched.add(payment.id);
    });
    return matched;
  }, [eligibleBalancePayments, filteredSales, parseAmount]);

  const balancePaymentsFiltered = useMemo(
    () => eligibleBalancePayments.filter(
      (payment) => !matchedInitialReceiptPaymentIds.has(payment.id)
    ),
    [eligibleBalancePayments, matchedInitialReceiptPaymentIds]
  );
  const totalInitialReceiptsAmount = useMemo(
    () => filteredSales.reduce(
      (sum, sale) => sum + parseAmount(sale.received_amount), 0
    ),
    [filteredSales, parseAmount]
  );

  const totalBalancePaymentsAmount = balancePaymentsFiltered.reduce(
    (sum, p) => sum + parseAmount(p.amount),
    0
  );
  const totalSalePaymentsAmount = filteredPayments.reduce(
    (sum, p) => sum + parseAmount(p.amount),
    0
  );
  const totalRevenue =
    totalInitialReceiptsAmount + totalSalePaymentsAmount + totalBalancePaymentsAmount;

  const previousBalanceAmount = selectedCustomerObj
    ? parseAmount(selectedCustomerObj.opening_balance)
    : 0;
  const previousBalanceCurrency =
    selectedCustomerObj?.opening_balance_currency_details?.title || "";

  // When "All Customers": total opening balance across all customers (for report and PDF)
  const totalOpeningBalanceAmount = useMemo(() => {
    if (selectedCustomer) return 0;
    return (customers || []).reduce(
      (sum, c) => sum + parseAmount(c.opening_balance),
      0
    );
  }, [customers, parseAmount, selectedCustomer]);
  const totalOpeningBalanceByCurrency = useMemo(() => {
    if (selectedCustomer) return {};
    const byCur = {};
    (customers || []).forEach((c) => {
      const cur = c.opening_balance_currency_details?.title || "AF";
      byCur[cur] = (byCur[cur] || 0) + parseAmount(c.opening_balance);
    });
    return byCur;
  }, [customers, parseAmount, selectedCustomer]);

  const shopProfitRows = useMemo(() => {
    const byShop = {};

    (filteredSales || []).forEach((sale) => {
      const shopId = String(getRecordShopId(sale) || "unknown");
      if (!byShop[shopId]) {
        byShop[shopId] = {
          shopId,
          shopName: getRecordShopName(sale),
          sales: 0,
          purchases: 0,
          stockValue: 0,
          salesCount: 0,
        };
      }
      byShop[shopId].sales += getSaleTotal(sale);
      byShop[shopId].salesCount += 1;
      if (byShop[shopId].shopName === "Unknown Shop") {
        byShop[shopId].shopName = getRecordShopName(sale);
      }
    });

    (purchases || []).forEach((purchase) => {
      const shopId = String(getRecordShopId(purchase) || "unknown");
      if (!byShop[shopId]) {
        byShop[shopId] = {
          shopId,
          shopName: getRecordShopName(purchase),
          sales: 0,
          purchases: 0,
          stockValue: 0,
          salesCount: 0,
        };
      }
      byShop[shopId].purchases += getPurchaseTotal(purchase);
      if (byShop[shopId].shopName === "Unknown Shop") {
        byShop[shopId].shopName = getRecordShopName(purchase);
      }
    });

    (stocks || []).forEach((stock) => {
      const shopId = String(getRecordShopId(stock) || "unknown");
      if (!byShop[shopId]) {
        byShop[shopId] = { shopId, shopName: getRecordShopName(stock), sales: 0, purchases: 0, stockValue: 0, salesCount: 0 };
      }
      byShop[shopId].stockValue += getStockValue(stock);
    });

    return Object.values(byShop)
      .map((row) => ({
        ...row,
        benefit: row.sales - row.purchases + row.stockValue,
      }))
      .sort((a, b) => a.shopName.localeCompare(b.shopName));
  }, [filteredSales, getPurchaseTotal, getSaleTotal, getStockValue, purchases, stocks]);

  const totalPurchasesAmount = useMemo(
    () => shopProfitRows.reduce((sum, row) => sum + row.purchases, 0),
    [shopProfitRows]
  );
  const totalStockValue = useMemo(
    () => shopProfitRows.reduce((sum, row) => sum + row.stockValue, 0),
    [shopProfitRows]
  );
  const benefitProfit = useMemo(
    () => shopProfitRows.reduce((sum, row) => sum + row.benefit, 0),
    [shopProfitRows]
  );

  const summary = {
    previous_balance: selectedCustomer
      ? previousBalanceAmount
      : totalOpeningBalanceAmount,
    previous_balance_currency: selectedCustomer
      ? previousBalanceCurrency
      : Object.keys(totalOpeningBalanceByCurrency).length === 1
        ? Object.keys(totalOpeningBalanceByCurrency)[0]
        : Object.keys(totalOpeningBalanceByCurrency).length > 1
          ? "Various"
          : "",
    total_opening_balance_by_currency: totalOpeningBalanceByCurrency,
    total_sales: {
      count: filteredSales.length,
      amount: filteredSales.reduce(
        (sum, s) => sum + getSaleSubtotal(s),
        0
      ),
      discount_amount: filteredSales.reduce(
        (sum, s) => sum + getSaleDiscount(s),
        0
      ),
      after_discount_amount: filteredSales.reduce(
        (sum, s) => sum + getSaleTotal(s),
        0
      ),
    },
    total_payments: {
      count: filteredPayments.length,
      amount: totalSalePaymentsAmount,
    },
    total_initial_receipts: totalInitialReceiptsAmount,
    total_balance_payments: {
      count: balancePaymentsFiltered.length,
      amount: totalBalancePaymentsAmount,
    },
    total_revenue: totalRevenue,
    total_returns: {
      count: filteredReturns.length,
      amount: filteredReturns.reduce(
        (sum, r) => sum + parseAmount(r.total_refound_amount),
        0
      ),
    },
    total_paid: filteredSales.reduce(
      (sum, s) => sum + getSalePaid(s),
      0
    ),
    total_remaining: filteredSales.reduce(
      (sum, s) => sum + parseAmount(s.total_remained_amount || 0),
      0
    ),
    outstanding_amount: filteredSales.reduce((sum, s) => {
      const saleAmount = getSaleTotal(s);
      const paidAmount = getSalePaid(s);
      const saleReturns = filteredReturns.filter(
        (r) => String(r.sale) === String(s.id)
      );
      const returnAmount = saleReturns.reduce(
        (rSum, r) => rSum + parseAmount(r.total_refound_amount),
        0
      );
      return sum + Math.max(0, saleAmount - paidAmount - returnAmount);
    }, 0),
    // Count direct sale receipts, later sale payments, and genuine general
    // balance payments exactly once.
    outstanding_including_previous: Math.max(
      0,
      (selectedCustomer ? previousBalanceAmount : totalOpeningBalanceAmount) +
        filteredSales.reduce((sum, sale) => sum + getSaleTotal(sale), 0) -
        filteredReturns.reduce(
          (sum, returnedSale) =>
            sum + parseAmount(returnedSale.total_refound_amount),
          0
        ) -
        totalInitialReceiptsAmount -
        totalSalePaymentsAmount -
        totalBalancePaymentsAmount
    ),
    net_sales:
      filteredSales.reduce((sum, s) => sum + getSaleTotal(s), 0) -
      filteredReturns.reduce(
        (sum, r) => sum + parseAmount(r.total_refound_amount),
        0
      ),
    payment_status: {
      paid: filteredSales.filter((s) => {
        const saleAmount = getSaleTotal(s);
        const paidAmount = getSalePaid(s);
        const saleReturns = filteredReturns.filter(
          (r) => String(r.sale) === String(s.id)
        );
        const returnAmount = saleReturns.reduce(
          (rSum, r) => rSum + parseAmount(r.total_refound_amount),
          0
        );
        return saleAmount - paidAmount - returnAmount <= 0;
      }).length,
      partial: filteredSales.filter((s) => {
        const saleAmount = getSaleTotal(s);
        const paidAmount = getSalePaid(s);
        const saleReturns = filteredReturns.filter(
          (r) => String(r.sale) === String(s.id)
        );
        const returnAmount = saleReturns.reduce(
          (rSum, r) => rSum + parseAmount(r.total_refound_amount),
          0
        );
        const remaining = saleAmount - paidAmount - returnAmount;
        return remaining > 0 && paidAmount > 0;
      }).length,
      unpaid: filteredSales.filter((s) => getSalePaid(s) === 0)
        .length,
    },
    is_all_customers: !selectedCustomer,
    selected_shop_name: selectedShopOption?.name || "All Shops",
    total_purchases: totalPurchasesAmount,
    total_stock_value: totalStockValue,
    benefit_profit: benefitProfit,
    shop_profit_rows: shopProfitRows,
  };

  const displayOutstanding =
    summary.outstanding_including_previous != null
      ? summary.outstanding_including_previous
      : summary.outstanding_amount;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCustomerChange = (event, newValue) => {
    setSelectedCustomer(newValue?.id ? String(newValue.id) : "");
  };

  const handleShopChange = (event, newValue) => {
    const nextId = newValue?.id ? String(newValue.id) : "";
    setSelectedShop(nextId);
    setShopSearchTerm(newValue?.name || "");
    setSelectedCustomer("");
  };

  const exportToPDF = () => {
    const exportData = {
      sales: filteredSales,
      payments: filteredPayments,
      returns: filteredReturns,
      balancePayments: balancePaymentsFiltered,
      summary,
      customers,
      selectedShopName: selectedShopOption?.name || "All Shops",
    };
    exportComprehensiveSaleReportToPDF(exportData);
  };

  const exportToExcel = () => {
    const exportData = {
      sales: filteredSales,
      payments: filteredPayments,
      returns: filteredReturns,
      balancePayments: balancePaymentsFiltered,
      summary,
      selectedCustomerName: selectedCustomerObj?.name || "",
      selectedShopName: selectedShopOption?.name || "All Shops",
      startDate: startDate?.format("YYYY-MM-DD") || "",
      endDate: endDate?.format("YYYY-MM-DD") || "",
    };
    exportComprehensiveSaleReportToExcel(exportData);
  };

  const exportCustomerReport = () => {
    if (customerReportData) {
      exportCustomerReportToPDF(customerReportData);
    }
  };

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedShop("");
    setShopSearchTerm("");
    setSelectedCustomer("");
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography>Loading sales data...</Typography>
      </Box>
    );
  }

  const tableHeadCellSx = {
    backgroundColor: "#12344d",
    color: "#ffffff",
    fontWeight: 700,
  };
  const totalRowSx = {
    backgroundColor: "#e8f1fb",
    "& .MuiTableCell-root": {
      color: "#102a43",
      fontWeight: 700,
    },
  };

  return (
    <Box sx={{ width: "100%", color: "#102a43" }}>
      <Typography variant="h4" sx={{ mb: 3, color: "#102a43", fontWeight: 700 }}>
        Comprehensive Sales Report
      </Typography>

      {/* View Mode Toggle */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
        <Button
          variant={viewMode === "simple" ? "contained" : "outlined"}
          onClick={() => setViewMode("simple")}
          sx={{ mr: 1 }}
        >
          📊 Simple View
        </Button>
        <Button
          variant={viewMode === "detailed" ? "contained" : "outlined"}
          onClick={() => setViewMode("detailed")}
        >
          📋 Detailed View
        </Button>
      </Box>

      {viewMode === "simple" ? (
        /* Simple View */
        <Box>
          {/* Filters for Simple View */}
          <Paper
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: "#f8fbff",
              border: "1px solid #d9e2ec",
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={shopOptions}
                    value={selectedShopOption}
                    onChange={handleShopChange}
                    onInputChange={(event, value, reason) => {
                      if (reason === "input") setShopSearchTerm(value);
                      if (reason === "clear") setShopSearchTerm("");
                    }}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.id ?? "") === String(value?.id ?? "")
                    }
                    loading={shopsLoading}
                    clearOnBlur={false}
                    renderInput={(params) => (
                      <TextField {...params} label="Shop" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={customerOptions}
                    value={selectedCustomerOption}
                    onChange={handleCustomerChange}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.id ?? "") === String(value?.id ?? "")
                    }
                    clearOnBlur={false}
                    renderInput={(params) => (
                      <TextField {...params} label="Customer" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button variant="outlined" onClick={clearFilters} fullWidth>
                    Clear
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    onClick={exportToPDF}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Export PDF
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    onClick={exportToExcel}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Export Excel
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    onClick={exportCustomerReport}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Customer Report
                  </Button>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Paper>

          <Paper
            sx={{
              p: 3,
              mb: 3,
              textAlign: "center",
              backgroundColor: "#f8fbff",
              border: "1px solid #d9e2ec",
            }}
          >
            <Typography
              variant="h5"
              sx={{ mb: 2, color: "#102a43", fontWeight: 700 }}
            >
              📈 Quick Overview
            </Typography>
            <Grid container spacing={2}>
              {(selectedCustomer || totalOpeningBalanceAmount > 0) && (
                <Grid item xs={12} md={2.4}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "#243b53",
                      borderRadius: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "white", fontWeight: "bold" }}
                    >
                      {summary.previous_balance.toFixed(2)}{" "}
                      {summary.previous_balance_currency || ""}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "white" }}>
                      Previous Balance (Opening)
                    </Typography>
                    <Typography variant="caption" sx={{ color: "white" }}>
                      {selectedCustomer
                        ? selectedCustomerObj?.name || "Customer"
                        : "All customers"}
                    </Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#0f4c81",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {summary.total_sales.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Total Sales
                  </Typography>
                  <Typography variant="h6" sx={{ color: "white" }}>
                    {summary.total_sales.amount.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "white" }}>
                    Discount: {summary.total_sales.discount_amount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#1f7a4c",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {summary.total_revenue.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Revenue (Collected)
                  </Typography>
                  <Typography variant="caption" sx={{ color: "white" }}>
                    Sale payments + Balance payments
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#8a6d1f",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {displayOutstanding.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Outstanding
                  </Typography>
                  <Typography variant="caption" sx={{ color: "white" }}>
                    {selectedCustomer
                      ? "Previous + Sales (after returns)"
                      : "Opening balance + Sales (after returns)"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#176087",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {summary.total_returns.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Returns
                  </Typography>
                  <Typography variant="h6" sx={{ color: "white" }}>
                    {summary.total_returns.amount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#5b2a86",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {summary.total_balance_payments.count}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Balance Payments
                  </Typography>
                  <Typography variant="h6" sx={{ color: "white" }}>
                    {summary.total_balance_payments.amount.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#365314",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: "white", fontWeight: "bold" }}
                  >
                    {formatProfit(summary.benefit_profit)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    Benefit / Profit
                  </Typography>
                  <Typography variant="caption" sx={{ color: "white" }}>
                    Sales − Purchases + Stock Value
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#334e68",
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "white", mb: 1 }}>
                    Payment Status
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "white", display: "block" }}
                  >
                    ✅ Paid: {summary.payment_status.paid}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "white", display: "block" }}
                  >
                    ⏳ Partial: {summary.payment_status.partial}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "white", display: "block" }}
                  >
                    ❌ Unpaid: {summary.payment_status.unpaid}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Paper
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: "#ffffff",
              border: "1px solid #d9e2ec",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, color: "#102a43", fontWeight: 700 }}>
              Benefit / Profit by Shop
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>Shop</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right">Sales</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right">Purchases</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right">Stock Value</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right">Benefit / Profit</TableCell>
                    <TableCell sx={tableHeadCellSx} align="right">Sales Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.shop_profit_rows.map((row) => (
                    <TableRow key={row.shopId}>
                      <TableCell>{row.shopName}</TableCell>
                      <TableCell align="right">{row.sales.toFixed(2)}</TableCell>
                      <TableCell align="right">{row.purchases.toFixed(2)}</TableCell>
                      <TableCell align="right">{row.stockValue.toFixed(2)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: row.benefit < 0 ? "error.main" : "success.main", fontWeight: 700 }}
                      >
                        {formatProfit(row.benefit)}
                      </TableCell>
                      <TableCell align="right">{row.salesCount}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={totalRowSx}>
                    <TableCell>Total</TableCell>
                    <TableCell align="right">{summary.total_sales.after_discount_amount.toFixed(2)}</TableCell>
                    <TableCell align="right">{summary.total_purchases.toFixed(2)}</TableCell>
                    <TableCell align="right">{summary.total_stock_value.toFixed(2)}</TableCell>
                    <TableCell align="right">{formatProfit(summary.benefit_profit)}</TableCell>
                    <TableCell align="right">{filteredSales.length}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper
            sx={{
              p: 2,
              backgroundColor: "#ffffff",
              border: "1px solid #d9e2ec",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Recent Sales
            </Typography>
            {filteredSales.slice(0, 5).map((sale) => {
              const saleReturns = filteredReturns.filter(
                (r) => String(r.sale) === String(sale.id)
              );
              const returnAmount = saleReturns.reduce(
                (sum, r) => sum + parseAmount(r.total_refound_amount),
                0
              );
              const remaining =
                getSaleTotal(sale) -
                getSalePaid(sale) -
                returnAmount;

              return (
                <Box
                  key={sale.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    mb: 1,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      #{sale.id} -{" "}
                      {sale.customer_details?.name || "Walk-in Customer"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {sale.sale_date}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Note / Hand Bill No.: {sale.notes || "-"}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="h6" color="primary">
                      {getSaleSubtotal(sale).toFixed(2)}
                    </Typography>
                    <Chip
                      label={remaining <= 0 ? "✅ Paid" : "⏳ Pending"}
                      size="small"
                      color={remaining <= 0 ? "success" : "warning"}
                    />
                  </Box>
                </Box>
              );
            })}
          </Paper>
        </Box>
      ) : (
        /* Detailed View */
        <Box>
          {/* Comprehensive Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {selectedCustomer && (
              <Grid item xs={12} md={2}>
                <Card sx={{ bgcolor: "grey.700" }}>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography variant="h6" sx={{ color: "white" }}>
                      Previous Balance (Opening)
                    </Typography>
                    <Typography variant="h4" sx={{ color: "white" }}>
                      {summary.previous_balance.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "white" }}>
                      {summary.previous_balance_currency || "—"} •{" "}
                      {selectedCustomerObj?.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: "primary.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="primary">
                    Total Sales
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {summary.total_sales.count}
                  </Typography>
                  <Typography variant="body2">
                    Amount: {summary.total_sales.amount.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: "success.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="success.main">
                    Revenue
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {summary.total_revenue.toFixed(2)}
                  </Typography>
                  <Typography variant="body2">
                    Sale + Balance payments
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: "warning.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="warning.main">
                    Outstanding
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {displayOutstanding.toFixed(2)}
                  </Typography>
                  <Typography variant="body2">
                    {selectedCustomer ? "Previous + Sales" : "After Returns"}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: "info.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="info.main">
                    Total Returns
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {summary.total_returns.count}
                  </Typography>
                  <Typography variant="body2">
                    Amount: {summary.total_returns.amount.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: "secondary.light" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="secondary.main">
                    Balance Payments
                  </Typography>
                  <Typography variant="h4" color="secondary.main">
                    {summary.total_balance_payments.count}
                  </Typography>
                  <Typography variant="body2">
                    Amount: {summary.total_balance_payments.amount.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={2}>
              <Card sx={{ bgcolor: "grey.200" }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" color="text.secondary">
                    Net Sales
                  </Typography>
                  <Typography variant="h4" color="text.secondary">
                    {summary.net_sales.toFixed(2)}
                  </Typography>
                  <Typography variant="body2">After Returns</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Financial Summary */}
          <Paper
            sx={{
              p: 3,
              mb: 3,
              bgcolor: "#ffffff",
              border: "1px solid #d9e2ec",
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 2, textAlign: "center", color: "#102a43", fontWeight: 700 }}
            >
              💰 Complete Financial Overview
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    border: "2px solid",
                    borderColor: "primary.main",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                    Sales Summary
                  </Typography>
                  {selectedCustomer && (
                    <Typography variant="body1" sx={{ mb: 0.5 }}>
                      Previous Balance (Opening):{" "}
                      <strong>
                        {summary.previous_balance.toFixed(2)}{" "}
                        {summary.previous_balance_currency || ""}
                      </strong>
                    </Typography>
                  )}
                  <Typography variant="body1">
                    Invoice Subtotal:{" "}
                    <strong>{summary.total_sales.amount.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Discount Amount:{" "}
                    <strong>{summary.total_sales.discount_amount.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Paid Amount:{" "}
                    <strong>{summary.total_paid.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Outstanding Amount:{" "}
                    <strong>{displayOutstanding.toFixed(2)}</strong>
                  </Typography>
                  {selectedCustomer && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      (Previous {summary.previous_balance.toFixed(2)} + Sales outstanding {summary.outstanding_amount.toFixed(2)})
                    </Typography>
                  )}
                  {selectedCustomer &&
                    selectedCustomerObj?.total_owed_by_currency &&
                    typeof selectedCustomerObj.total_owed_by_currency ===
                      "object" && (
                      <Typography
                        variant="body1"
                        sx={{ mt: 1, fontWeight: "bold" }}
                      >
                        Total Owed (customer):{" "}
                        {Object.entries(
                          selectedCustomerObj.total_owed_by_currency
                        )
                          .map(([cur, amt]) => `${Number(amt).toFixed(2)} ${cur}`)
                          .join(", ") || "—"}
                      </Typography>
                    )}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Collection Progress:{" "}
                    {summary.total_sales.after_discount_amount > 0
                      ? (
                          (summary.total_paid /
                            summary.total_sales.after_discount_amount) *
                          100
                        ).toFixed(1)
                      : 0}
                    %
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    border: "2px solid",
                    borderColor: "success.main",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>
                    Revenue (Collected)
                  </Typography>
                  <Typography variant="body1">
                    Sale Payments:{" "}
                    <strong>{summary.total_payments.amount.toFixed(2)}</strong>{" "}
                    ({summary.total_payments.count})
                  </Typography>
                  <Typography variant="body1">
                    Balance Payments:{" "}
                    <strong>
                      {summary.total_balance_payments.amount.toFixed(2)}
                    </strong>{" "}
                    ({summary.total_balance_payments.count})
                  </Typography>
                  <Typography variant="body1">
                    Total Revenue:{" "}
                    <strong>{summary.total_revenue.toFixed(2)}</strong>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    border: "2px solid",
                    borderColor: "info.main",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6" color="info.main" sx={{ mb: 1 }}>
                    Returns & Net
                  </Typography>
                  <Typography variant="body1">
                    Total Returns:{" "}
                    <strong>{summary.total_returns.amount.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Net Sales Value:{" "}
                    <strong>{summary.net_sales.toFixed(2)}</strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Note: Returns are deducted from outstanding amount for
                    accurate balance calculation
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    border: "2px solid",
                    borderColor: summary.benefit_profit < 0 ? "error.main" : "success.main",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    color={summary.benefit_profit < 0 ? "error.main" : "success.main"}
                    sx={{ mb: 1 }}
                  >
                    Benefit / Profit
                  </Typography>
                  <Typography variant="body1">
                    Sales Amount:{" "}
                    <strong>{summary.total_sales.after_discount_amount.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Purchases:{" "}
                    <strong>{summary.total_purchases.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body1">
                    Benefit / Profit:{" "}
                    <strong>{formatProfit(summary.benefit_profit)}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Sales − Purchases + current Stock Value for the selected shop/date filters
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Filters */}
          <Box sx={{ mb: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={shopOptions}
                    value={selectedShopOption}
                    onChange={handleShopChange}
                    onInputChange={(event, value, reason) => {
                      if (reason === "input") setShopSearchTerm(value);
                      if (reason === "clear") setShopSearchTerm("");
                    }}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.id ?? "") === String(value?.id ?? "")
                    }
                    loading={shopsLoading}
                    clearOnBlur={false}
                    renderInput={(params) => (
                      <TextField {...params} label="Shop" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={customerOptions}
                    value={selectedCustomerOption}
                    onChange={handleCustomerChange}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.id ?? "") === String(value?.id ?? "")
                    }
                    clearOnBlur={false}
                    renderInput={(params) => (
                      <TextField {...params} label="Customer" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button variant="outlined" onClick={clearFilters} fullWidth>
                    Clear
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    onClick={exportToPDF}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Export PDF
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="outlined"
                    onClick={exportToExcel}
                    startIcon={<Download />}
                    fullWidth
                  >
                    Export Excel
                  </Button>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>

          {/* Tabs */}
          <Paper sx={{ width: "100%" }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label={`Sales (${filteredSales.length})`} />
              <Tab label={`Sale Payments (${filteredPayments.length})`} />
              <Tab label={`Returns (${filteredReturns.length})`} />
              <Tab
                label={`Balance Payments (${balancePaymentsFiltered.length})`}
              />
              <Tab label={`Benefit / Profit (${summary.shop_profit_rows.length})`} />
            </Tabs>

            {/* Sales Tab */}
            <TabPanel value={tabValue} index={0}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>ID</TableCell>
                      <TableCell sx={tableHeadCellSx}>Customer</TableCell>
                      <TableCell sx={tableHeadCellSx}>Date</TableCell>
                      <TableCell sx={tableHeadCellSx}>Note / Hand Bill No.</TableCell>
                      <TableCell sx={tableHeadCellSx}>Total Amount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Discount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Paid</TableCell>
                      <TableCell sx={tableHeadCellSx}>Returns</TableCell>
                      <TableCell sx={tableHeadCellSx}>Outstanding</TableCell>
                      <TableCell sx={tableHeadCellSx}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSales.map((sale) => {
                      const saleReturns = filteredReturns.filter(
                        (r) => String(r.sale) === String(sale.id)
                      );
                      const returnAmount = saleReturns.reduce(
                        (sum, r) => sum + parseAmount(r.total_refound_amount),
                        0
                      );
                      const paidAmount = getSalePaid(sale);
                      const totalAmount = getSaleSubtotal(sale);
                      const discountAmount = getSaleDiscount(sale);
                      const amountAfterDiscount = getSaleTotal(sale);
                      const outstanding = Math.max(
                        0,
                        amountAfterDiscount - paidAmount - returnAmount
                      );
                      const status =
                        outstanding <= 0
                          ? "Paid"
                          : paidAmount > 0
                          ? "Partial"
                          : "Unpaid";
                      const statusColor =
                        outstanding <= 0
                          ? "success"
                          : paidAmount > 0
                          ? "warning"
                          : "error";

                      return (
                        <TableRow key={sale.id}>
                          <TableCell>#{sale.id}</TableCell>
                          <TableCell>
                            {sale.customer_details?.name || "Walk-in Customer"}
                          </TableCell>
                          <TableCell>{sale.sale_date}</TableCell>
                          <TableCell>{sale.notes || "-"}</TableCell>
                          <TableCell>{totalAmount.toFixed(2)}</TableCell>
                          <TableCell>{discountAmount.toFixed(2)}</TableCell>
                          <TableCell>{paidAmount.toFixed(2)}</TableCell>
                          <TableCell>{returnAmount.toFixed(2)}</TableCell>
                          <TableCell>{outstanding.toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip
                              label={status}
                              size="small"
                              color={statusColor}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow sx={totalRowSx}>
                      <TableCell colSpan={4}>
                        <strong>TOTAL ({filteredSales.length} sales)</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{summary.total_sales.amount.toFixed(2)}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {summary.total_sales.discount_amount.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>{summary.total_paid.toFixed(2)}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {summary.total_returns.amount.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>{displayOutstanding.toFixed(2)}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Outstanding</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Payments Tab */}
            <TabPanel value={tabValue} index={1}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>ID</TableCell>
                      <TableCell sx={tableHeadCellSx}>Sale</TableCell>
                      <TableCell sx={tableHeadCellSx}>Amount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Date</TableCell>
                      <TableCell sx={tableHeadCellSx}>Method</TableCell>
                      <TableCell sx={tableHeadCellSx}>Currency</TableCell>
                      <TableCell sx={tableHeadCellSx}>Note / Bill No.</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPayments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>#{payment.id}</TableCell>
                        <TableCell>Sale #{payment.sale}</TableCell>
                        <TableCell>
                          {parseAmount(payment.amount).toFixed(2)}
                        </TableCell>
                        <TableCell>{payment.payment_date}</TableCell>
                        <TableCell>
                          {payment.payment_method || "Cash"}
                        </TableCell>
                        <TableCell>
                          {payment.currency_details?.title || "USD"}
                        </TableCell>
                        <TableCell>{payment.notes || "-"}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={totalRowSx}>
                      <TableCell colSpan={2}>
                        <strong>
                          TOTAL ({filteredPayments.length} payments)
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {summary.total_payments.amount.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell colSpan={4}>
                        <strong>Total Collected</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Returns Tab */}
            <TabPanel value={tabValue} index={2}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>ID</TableCell>
                      <TableCell sx={tableHeadCellSx}>Sale</TableCell>
                      <TableCell sx={tableHeadCellSx}>Return Date</TableCell>
                      <TableCell sx={tableHeadCellSx}>Reason</TableCell>
                      <TableCell sx={tableHeadCellSx}>Refund Amount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Items</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReturns.map((returnItem) => (
                      <TableRow key={returnItem.id}>
                        <TableCell>#{returnItem.id}</TableCell>
                        <TableCell>Sale #{returnItem.sale}</TableCell>
                        <TableCell>{returnItem.return_date}</TableCell>
                        <TableCell>
                          {returnItem.reason || "No reason provided"}
                        </TableCell>
                        <TableCell>
                          {parseAmount(returnItem.total_refound_amount).toFixed(
                            2
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={returnItem.return_items?.length || 0}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={totalRowSx}>
                      <TableCell colSpan={4}>
                        <strong>
                          TOTAL ({filteredReturns.length} returns)
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {summary.total_returns.amount.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {filteredReturns.reduce(
                            (sum, r) => sum + (r.return_items?.length || 0),
                            0
                          )}{" "}
                          items returned
                        </strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            {/* Balance Payments Tab */}
            <TabPanel value={tabValue} index={3}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>ID</TableCell>
                      <TableCell sx={tableHeadCellSx}>Customer</TableCell>
                      <TableCell sx={tableHeadCellSx}>Amount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Date</TableCell>
                      <TableCell sx={tableHeadCellSx}>Currency</TableCell>
                      <TableCell sx={tableHeadCellSx}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {balancePaymentsFiltered.map((bp) => (
                      <TableRow key={bp.id}>
                        <TableCell>#{bp.id}</TableCell>
                        <TableCell>
                          {bp.customer_details?.name || "-"}
                        </TableCell>
                        <TableCell>
                          {parseAmount(bp.amount).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {bp.payment_date
                            ? new Date(bp.payment_date).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {bp.currency_details?.title || "-"}
                        </TableCell>
                        <TableCell>{bp.notes || "-"}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={totalRowSx}>
                      <TableCell colSpan={2}>
                        <strong>
                          TOTAL ({balancePaymentsFiltered.length} balance
                          payments)
                        </strong>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {summary.total_balance_payments.amount.toFixed(2)}
                        </strong>
                      </TableCell>
                      <TableCell colSpan={3}>
                        <strong>Total collected (balance)</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            <TabPanel value={tabValue} index={4}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeadCellSx}>Shop</TableCell>
                      <TableCell sx={tableHeadCellSx}>Sales Amount</TableCell>
                      <TableCell sx={tableHeadCellSx}>Purchases</TableCell>
                      <TableCell sx={tableHeadCellSx}>Stock Value</TableCell>
                      <TableCell sx={tableHeadCellSx}>Benefit / Profit</TableCell>
                      <TableCell sx={tableHeadCellSx}>Sales Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary.shop_profit_rows.map((row) => (
                      <TableRow key={row.shopId}>
                        <TableCell>{row.shopName}</TableCell>
                        <TableCell>{row.sales.toFixed(2)}</TableCell>
                        <TableCell>{row.purchases.toFixed(2)}</TableCell>
                        <TableCell>{row.stockValue.toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip
                            label={formatProfit(row.benefit)}
                            color={row.benefit < 0 ? "error" : "success"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{row.salesCount}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={totalRowSx}>
                      <TableCell>
                        <strong>Total</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{summary.total_sales.after_discount_amount.toFixed(2)}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{summary.total_purchases.toFixed(2)}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{summary.total_stock_value.toFixed(2)}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{formatProfit(summary.benefit_profit)}</strong>
                      </TableCell>
                      <TableCell>
                        <strong>{filteredSales.length}</strong>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default ComprehensiveSaleReport;
